import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { negotiationService } from '../../../services/negotiationService';
import { clientService } from '../../../services/clientService';
import { attachmentService } from '../../../services/attachmentService';

const EditNegotiationModal = ({ isOpen, onClose, onSuccess, negotiation }) => {
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    destination: '',
    travel_date: '',
    return_date: '',
    sale_date: '',
    sale_value: '',
    supplier_cost: '',
    notes: ''
  });

  // Add payments state for up to 3 payment entries with payment_status
  const [payments, setPayments] = useState([
    { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' },
    { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' },
    { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' }
  ]);
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingClients, setLoadingClients] = useState(true);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [taxDocumentFile, setTaxDocumentFile] = useState(null);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const formatBrazilianCurrency = (value) => {
    if (!value) return '';
    
    const numericValue = value?.toString()?.replace(/\D/g, '');
    const numberValue = parseFloat(numericValue) / 100;
    
    return numberValue?.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseBrazilianCurrency = (value) => {
    if (!value) return 0;
    const normalized = value?.toString()?.replace(/\./g, '')?.replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e?.target;
    const formatted = formatBrazilianCurrency(value);
    
    setFormData(prev => ({
      ...prev,
      [name]: formatted
    }));
  };

  const isFormValid = () => {
    const saleValue = parseBrazilianCurrency(formData?.sale_value);
    // Remove profit validation - allow editing even with negative profit
    return (
      formData?.title?.trim() !== '' &&
      formData?.client_id !== '' && 
      saleValue > 0
    );
  };

  useEffect(() => {
    if (isOpen) {
      loadClients();
      if (negotiation) {
        // Convert values to Brazilian currency format for display
        const saleValueFormatted = (negotiation?.sale_value || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        
        const supplierCostFormatted = (negotiation?.supplier_cost || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        setFormData({
          title: negotiation?.title || '',
          client_id: negotiation?.client_id || '',
          destination: negotiation?.destination || '',
          travel_date: negotiation?.travel_date || '',
          return_date: negotiation?.return_date || '',
          sale_date: negotiation?.sale_date || '',
          sale_value: saleValueFormatted,
          supplier_cost: supplierCostFormatted,
          notes: negotiation?.notes || ''
        });

        // Load existing receivables into payments state
        loadReceivables(negotiation?.id);

        // Load existing attachments
        loadAttachments(negotiation?.id);
      }
    }
  }, [isOpen, negotiation]);

  const loadAttachments = async (negotiationId) => {
    try {
      setLoadingAttachments(true);
      const attachments = await attachmentService?.getNegotiationAttachments(negotiationId);
      setExistingAttachments(attachments);
    } catch (err) {
      console.error('Error loading attachments:', err);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const loadReceivables = async (negotiationId) => {
    try {
      const data = await negotiationService?.getById(negotiationId);
      const existingReceivables = data?.receivables || [];
      
      // Map existing receivables to payment format
      const mappedPayments = [
        { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' },
        { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' },
        { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' }
      ];

      existingReceivables?.forEach((receivable, index) => {
        if (index < 3) {
          // FIXED: Dynamically calculate status based on due_date if status is 'pending'
          let calculatedStatus = receivable?.status || 'pending';
          
          // If status is pending, check if it's actually overdue
          if (calculatedStatus === 'pending' && receivable?.due_date) {
            const dueDate = new Date(receivable?.due_date);
            const today = new Date();
            today?.setHours(0, 0, 0, 0);
            dueDate?.setHours(0, 0, 0, 0);
            
            // If due date has passed, mark as overdue
            if (dueDate < today) {
              calculatedStatus = 'overdue';
            }
          }

          mappedPayments[index] = {
            id: receivable?.id,
            payment_method: receivable?.payment_method || 'credit_card',
            due_date: receivable?.due_date || '',
            operator: receivable?.operator || '',
            amount: (receivable?.amount || 0)?.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }),
            payment_status: calculatedStatus // FIXED: Use calculated status
          };
        }
      });

      setPayments(mappedPayments);
    } catch (err) {
      console.error('Error loading receivables:', err);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const data = await clientService?.getAll();
      setClients(data || []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Erro ao carregar clientes');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentChange = (index, field, value) => {
    setPayments(prev => {
      const updated = [...prev];
      if (field === 'reset') {
        updated[index] = { id: null, payment_method: 'credit_card', due_date: '', operator: '', amount: '', payment_status: 'pending' };
      } else {
        updated[index] = { ...updated?.[index], [field]: value };
      }
      return updated;
    });
  };

  const handleFileChange = (e, fileType) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file?.size > 10485760) {
        setError(`Arquivo muito grande. Tamanho máximo: 10MB`);
        return;
      }

      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes?.includes(file?.type)) {
        setError('Formato de arquivo inválido. Use PDF, JPG, PNG ou WebP');
        return;
      }

      if (fileType === 'invoice') {
        setInvoiceFile(file);
      } else if (fileType === 'tax_document') {
        setTaxDocumentFile(file);
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Tem certeza que deseja deletar este arquivo?')) {
      return;
    }

    try {
      await attachmentService?.deleteAttachment(attachmentId);
      setExistingAttachments(prev => prev?.filter(att => att?.id !== attachmentId));
    } catch (err) {
      setError(err?.message || 'Erro ao deletar arquivo');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const saleValue = parseBrazilianCurrency(formData?.sale_value);
    
    if (!formData?.title || !formData?.client_id || saleValue <= 0) {
      setError('Por favor, preencha todos os campos obrigatórios. O valor da venda deve ser maior que zero.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const dataToSubmit = {
        clientId: formData?.client_id,
        title: formData?.title,
        destination: formData?.destination,
        saleValue: saleValue,
        supplierCost: formData?.supplier_cost ? parseBrazilianCurrency(formData?.supplier_cost) : 0,
        saleDate: formData?.sale_date,
        notes: formData?.notes
      };

      await negotiationService?.update(negotiation?.id, dataToSubmit);

      // Update receivables
      const validPayments = payments?.filter(p => 
        p?.payment_method && p?.due_date && p?.operator && p?.amount
      );

      if (validPayments?.length > 0) {
        await negotiationService?.updateReceivables(negotiation?.id, validPayments?.map(p => ({
          id: p?.id,
          paymentMethod: p?.payment_method,
          dueDate: p?.due_date,
          operator: p?.operator,
          amount: parseBrazilianCurrency(p?.amount),
          paymentStatus: p?.payment_status
        })));
      }

      // Upload new files if provided
      if (invoiceFile || taxDocumentFile) {
        setUploadingFiles(true);
        
        try {
          if (invoiceFile) {
            await attachmentService?.uploadNegotiationDocument(
              invoiceFile, 
              negotiation?.id, 
              'invoice'
            );
          }

          if (taxDocumentFile) {
            await attachmentService?.uploadNegotiationDocument(
              taxDocumentFile, 
              negotiation?.id, 
              'tax_document'
            );
          }
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          setError('Negociação atualizada, mas houve erro ao fazer upload dos arquivos. Tente novamente.');
        }
      }
      
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Erro ao atualizar negociação');
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  const calculateMargin = () => {
    const sale = parseBrazilianCurrency(formData?.sale_value) || 0;
    const cost = parseBrazilianCurrency(formData?.supplier_cost) || 0;
    if (sale > 0) {
      const margin = ((sale - cost) / sale) * 100;
      return margin?.toFixed(1);
    }
    return '0.0';
  };

  const calculateProfit = () => {
    // Calculate profit as total payment amounts
    const totalPayments = payments?.reduce((sum, p) => 
      sum + (parseBrazilianCurrency(p?.amount) || 0), 0
    );
    return totalPayments?.toFixed(2);
  };

  if (!isOpen || !negotiation) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Edit" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Editar Negociação</h2>
              <p className="text-sm text-muted-foreground">#{negotiation?.id?.slice(0, 8)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={18} />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Informações Básicas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Título da Negociação *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData?.title}
                    onChange={handleChange}
                    placeholder="Ex: Pacote Caribe - João Silva"
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cliente *
                  </label>
                  <select
                    name="client_id"
                    value={formData?.client_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                    disabled={loadingClients}
                  >
                    <option value="">
                      {loadingClients ? 'Carregando...' : 'Selecione um cliente'}
                    </option>
                    {clients?.map(client => (
                      <option key={client?.id} value={client?.id}>
                        {client?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Destino
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData?.destination}
                    onChange={handleChange}
                    placeholder="Ex: Cancún, México"
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data da Viagem
                  </label>
                  <input
                    type="date"
                    name="travel_date"
                    value={formData?.travel_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data de Retorno
                  </label>
                  <input
                    type="date"
                    name="return_date"
                    value={formData?.return_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Payment Information Section */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Formas de Pagamento
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (Edite até 3 formas de pagamento)
                </span>
              </h3>

              <div className="space-y-4">
                {payments?.map((payment, index) => (
                  <div key={index} className="p-4 bg-muted/20 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-foreground">Pagamento {index + 1}</h4>
                      {payment?.payment_method || payment?.due_date || payment?.operator || payment?.amount ? (
                        <button
                          type="button"
                          onClick={() => handlePaymentChange(index, 'reset', true)}
                          className="text-xs text-muted-foreground hover:text-error"
                        >
                          Limpar
                        </button>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Forma de Pagamento
                        </label>
                        <select
                          value={payment?.payment_method}
                          onChange={(e) => handlePaymentChange(index, 'payment_method', e?.target?.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Selecione</option>
                          <option value="credit_card">Cartão de Crédito</option>
                          <option value="debit_card">Cartão de Débito</option>
                          <option value="bank_transfer">Transferência Bancária</option>
                          <option value="pix">PIX</option>
                          <option value="cash">Dinheiro</option>
                          <option value="boleto">Boleto</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Status do Pagamento
                        </label>
                        <select
                          value={payment?.payment_status}
                          onChange={(e) => handlePaymentChange(index, 'payment_status', e?.target?.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="pending">Pendente</option>
                          <option value="partial">Parcial</option>
                          <option value="paid">Pago</option>
                          <option value="overdue">Atrasado</option>
                        </select>
                        {/* ADDED: Helper text to show dynamic status calculation */}
                        {payment?.due_date && payment?.payment_status === 'pending' && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {(() => {
                              const dueDate = new Date(payment?.due_date);
                              const today = new Date();
                              today?.setHours(0, 0, 0, 0);
                              dueDate?.setHours(0, 0, 0, 0);
                              
                              if (dueDate < today) {
                                return '⚠️ Vencimento passou - marque como "Pago" se já foi pago';
                              } else if (dueDate?.getTime() === today?.getTime()) {
                                return '📅 Vence hoje';
                              } else {
                                return '📆 A vencer';
                              }
                            })()}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Data de Vencimento
                        </label>
                        <input
                          type="date"
                          value={payment?.due_date}
                          onChange={(e) => handlePaymentChange(index, 'due_date', e?.target?.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Operadora
                        </label>
                        <input
                          type="text"
                          value={payment?.operator}
                          onChange={(e) => handlePaymentChange(index, 'operator', e?.target?.value)}
                          placeholder="Ex: CVC, Decolar, Latam"
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Valor
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                            R$
                          </span>
                          <input
                            type="text"
                            value={payment?.amount}
                            onChange={(e) => {
                              const formatted = formatBrazilianCurrency(e?.target?.value);
                              handlePaymentChange(index, 'amount', formatted);
                            }}
                            placeholder="0,00"
                            className="w-full pl-12 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Summary */}
              {payments?.some(p => p?.amount) && (
                <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Total dos Pagamentos:</span>
                    <span className="text-lg font-bold text-primary">
                      R$ {payments?.reduce((sum, p) => 
                        sum + (parseBrazilianCurrency(p?.amount) || 0), 0
                      )?.toFixed(2)}
                    </span>
                  </div>
                  {formData?.sale_value && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Valor da Venda:</span>
                      <span className="text-foreground">
                        R$ {parseBrazilianCurrency(formData?.sale_value)?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Financial Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Informações Financeiras</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Valor da Venda *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      R$
                    </span>
                    <input
                      type="text"
                      name="sale_value"
                      value={formData?.sale_value}
                      onChange={handleCurrencyChange}
                      placeholder="0,00"
                      className="w-full pl-12 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  {formData?.sale_value && parseBrazilianCurrency(formData?.sale_value) <= 0 && (
                    <p className="mt-1 text-xs text-error">
                      O valor deve ser maior que zero
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Custo do Fornecedor
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      R$
                    </span>
                    <input
                      type="text"
                      name="supplier_cost"
                      value={formData?.supplier_cost}
                      onChange={handleCurrencyChange}
                      placeholder="0,00"
                      className="w-full pl-12 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data da Venda
                  </label>
                  <input
                    type="date"
                    name="sale_date"
                    value={formData?.sale_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Financial Summary */}
              {payments?.some(p => p?.amount) && (
                <div className="mt-4 p-4 bg-muted/30 rounded-lg grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Lucro (Total de Pagamentos)</p>
                    <p className="text-lg font-bold text-foreground">
                      R$ {calculateProfit()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Margem</p>
                    <p className="text-lg font-bold text-foreground">
                      {calculateMargin()}%
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Observações</h3>
              <textarea
                name="notes"
                value={formData?.notes}
                onChange={handleChange}
                placeholder="Adicione observações sobre esta negociação..."
                rows={4}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {/* Document Attachments - NEW SECTION */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Documentos</h3>
              
              {/* Existing Attachments */}
              {loadingAttachments ? (
                <div className="flex items-center justify-center p-4 bg-muted/20 rounded-lg mb-4">
                  <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Carregando anexos...</span>
                </div>
              ) : existingAttachments?.length > 0 ? (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium text-foreground mb-2">Arquivos Anexados:</p>
                  {existingAttachments?.map((attachment) => (
                    <div key={attachment?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        <Icon 
                          name="FileText" 
                          size={18} 
                          className="text-primary" 
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {attachment?.document_type === 'invoice' ? 'Fatura Recebida' : 
                             attachment?.document_type === 'tax_document'? 'Nota Fiscal' : 'Outro Documento'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {attachment?.file_name} ({(attachment?.file_size / 1024)?.toFixed(1)} KB)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={attachment?.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <Icon name="Eye" size={16} />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(attachment?.id)}
                          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          title="Deletar"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* New File Upload */}
              <div className="grid grid-cols-2 gap-4">
                {/* Invoice Upload */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {existingAttachments?.some(a => a?.document_type === 'invoice') 
                      ? 'Substituir Fatura Recebida' :'Adicionar Fatura Recebida'}
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileChange(e, 'invoice')}
                      className="hidden"
                      id="invoice-upload"
                    />
                    <label
                      htmlFor="invoice-upload"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-background border border-dashed border-border rounded-lg hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors"
                    >
                      <Icon name="Upload" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {invoiceFile ? invoiceFile?.name : 'Selecionar arquivo'}
                      </span>
                    </label>
                  </div>
                  {invoiceFile && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-primary/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={16} className="text-primary" />
                        <span className="text-xs text-foreground truncate">
                          {invoiceFile?.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setInvoiceFile(null)}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    PDF, JPG, PNG ou WebP (máx. 10MB)
                  </p>
                </div>

                {/* Tax Document Upload */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {existingAttachments?.some(a => a?.document_type === 'tax_document') 
                      ? 'Substituir Nota Fiscal' :'Adicionar Nota Fiscal'}
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileChange(e, 'tax_document')}
                      className="hidden"
                      id="tax-document-upload"
                    />
                    <label
                      htmlFor="tax-document-upload"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-background border border-dashed border-border rounded-lg hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors"
                    >
                      <Icon name="Upload" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {taxDocumentFile ? taxDocumentFile?.name : 'Selecionar arquivo'}
                      </span>
                    </label>
                  </div>
                  {taxDocumentFile && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-primary/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={16} className="text-primary" />
                        <span className="text-xs text-foreground truncate">
                          {taxDocumentFile?.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTaxDocumentFile(null)}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    PDF, JPG, PNG ou WebP (máx. 10MB)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || loadingClients || !isFormValid()}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{uploadingFiles ? 'Enviando arquivos...' : 'Salvando...'}</span>
                </>
              ) : (
                <>
                  <Icon name="Check" size={18} />
                  <span>Salvar Alterações</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNegotiationModal;